/**
 * Preview Storage Shim (#312).
 *
 * Preview HTML runs in an OPAQUE-origin sandbox — `sandbox allow-scripts` with
 * NO `allow-same-origin`, on purpose: untrusted user HTML must never reach
 * same-origin powers, and a shared preview origin would let one uploaded page
 * read another's storage. The price is that in that document `localStorage`,
 * `sessionStorage` and `document.cookie` all THROW SecurityError on access. Most
 * SPA frameworks and many libraries touch one of them at init, so an ordinary
 * uploaded static site throws uncaught on its first line and renders blank.
 *
 * The fix is NOT `allow-same-origin`. Instead this script, injected FIRST in
 * <head> (before any page script), stands in a per-frame, per-load, in-memory
 * replacement — for each API ONLY when the real one throws. Nothing persists
 * and nothing is shared across frames, so no isolation is weakened; on a real
 * origin (a ppn.gg app, a normal browser tab) it installs nothing.
 *
 * `history.pushState/replaceState` with a relative URL throws in an inline srcdoc
 * preview (never on /s/, measured in Chrome): a call that throws SecurityError is
 * retried on the frame's own address (hash routes work), else kept as a
 * state-only replace, so the page keeps running. Out of scope,
 * deliberately: SPA routers on /s/ see `/s/<sid>/…` as their path (a base-path
 * issue, not a sandbox one — hash routing works; a real fix is serving the app
 * at an origin root, as ppn.gg does) and `indexedDB` (async, and its main
 * consumers already fall back).
 *
 * Known limits, accepted (review round 2):
 *   - Nothing survives a RELOAD, sessionStorage included (real sessionStorage
 *     does). A "reload once" guard kept in storage (e.g. a lazy-chunk retry)
 *     reloads every time. Persisting would need `window.name`, which pages use
 *     themselves; an author can already loop reloads, so this is UX, not load.
 *   - `Object.defineProperty(localStorage, k, …)` with `configurable: false` or a
 *     symbol key throws here (Proxy invariants); real Storage accepts it. Rare
 *     on localStorage; the common string/data define works.
 *   - Inline mode, a PATH route ('/login') still can't change the URL. A guard
 *     that retries it in an unbounded synchronous loop now spins where it used
 *     to die on the first SecurityError (it never adds history entries — the
 *     fallback is a replace). Such a page is broken either way, and any page
 *     can already spin its own frame; no cap, deliberately.
 *
 * It also reports a refused camera / microphone / screen request to the parent
 * (`papan:needs-app-origin`, #1408 stage 1) — the opaque origin is why it was
 * refused, and the app's own link is where it works. The error still reaches
 * the page unchanged.
 *
 * ⚠️ MUST STAY IMPORT-FREE — same rule as preview-console.ts. Exposed via the
 * client-safe `@papan/files/api/storage-shim-script` subpath and bundled into
 * the WEB app (HtmlViewer injects it into the inline srcDoc). The whole module
 * body is a template string. The script must never throw itself.
 */

/** Returns the storage shim as a `<script>` string to inject first in <head>. */
export function getSandboxStorageShimScript(): string {
  return `<script data-papan-storage-shim>
(function(){
  function throws(read){ try { read(); return false } catch (e) { return true } }

  // ─── Web Storage: Map-backed, behind a Proxy so property-style access works ─
  // (localStorage.foo = 'x', Object.keys(localStorage), 'foo' in localStorage).
  // The prototype chains to Storage.prototype so instanceof / toString match.
  function memoryStorage(){
    var m = new Map();
    var base = typeof Storage === 'function' ? Storage.prototype : Object.prototype;
    var proto = Object.create(base, {
      getItem: { value: function(k){ k = String(k); return m.has(k) ? m.get(k) : null } },
      setItem: { value: function(k, v){ m.set(String(k), String(v)) } },
      removeItem: { value: function(k){ m.delete(String(k)) } },
      clear: { value: function(){ m.clear() } },
      key: { value: function(i){ var ks = Array.from(m.keys()); i = Number(i) >>> 0; return i < ks.length ? ks[i] : null } },
      length: { get: function(){ return m.size } }
    });
    try { Object.defineProperty(proto, Symbol.toStringTag, { value: 'Storage' }) } catch (e) {}
    // Like real Storage: a string-keyed write or data-descriptor define goes to
    // the store (even one named like a method), a read prefers the prototype's
    // methods. Nothing ever lands on the Proxy target, so ownKeys can't violate
    // an invariant.
    function stored(p){ return typeof p === 'string' && !(p in proto) }
    return new Proxy(Object.create(proto), {
      get: function(t, p){ return stored(p) && m.has(p) ? m.get(p) : proto[p] },
      set: function(t, p, v){ if (typeof p === 'string') m.set(p, String(v)); return true },
      defineProperty: function(t, p, d){
        if (typeof p !== 'string' || !d || !('value' in d)) return false;
        m.set(p, String(d.value));
        return true;
      },
      has: function(t, p){ return p in proto || (typeof p === 'string' && m.has(p)) },
      deleteProperty: function(t, p){ if (typeof p === 'string') m.delete(p); return true },
      ownKeys: function(){ return Array.from(m.keys()) },
      getOwnPropertyDescriptor: function(t, p){
        if (typeof p === 'string' && m.has(p)) return { value: m.get(p), writable: true, enumerable: true, configurable: true };
      }
    });
  }
  ['localStorage', 'sessionStorage'].forEach(function(name){
    if (!throws(function(){ return window[name] })) return;
    try { Object.defineProperty(window, name, { value: memoryStorage(), configurable: true, enumerable: true, writable: true }) } catch (e) {}
  });

  // ─── document.cookie: an in-memory jar. Attributes are ignored except the two
  // that delete (Max-Age<=0, or a past Expires; Max-Age wins, RFC 6265 §5.3).
  // Installed when the real one THROWS (Chromium, opaque origin) or silently
  // drops a write (per spec an about:srcdoc document is "cookie-averse": the
  // getter returns "" and the setter is a no-op). The probe removes itself.
  function cookieWorks(){
    try {
      var probe = '__papan_cookie_probe';
      document.cookie = probe + '=1';
      var ok = document.cookie.indexOf(probe + '=1') !== -1;
      document.cookie = probe + '=; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      return ok;
    } catch (e) { return false }
  }
  if (!cookieWorks()) {
    var jar = new Map();
    try {
      Object.defineProperty(document, 'cookie', {
        configurable: true,
        enumerable: true,
        get: function(){
          var out = [];
          jar.forEach(function(v, k){ out.push(k ? k + '=' + v : v) });
          return out.join('; ');
        },
        set: function(s){
          var parts = String(s).split(';');
          var eq = parts[0].indexOf('=');
          var name = eq === -1 ? '' : parts[0].slice(0, eq).trim();
          var value = (eq === -1 ? parts[0] : parts[0].slice(eq + 1)).trim();
          if (!name && !value) return;
          var maxAge = null, expires = null;
          for (var i = 1; i < parts.length; i++) {
            var at = parts[i].indexOf('=');
            var key = (at === -1 ? parts[i] : parts[i].slice(0, at)).trim().toLowerCase();
            var val = at === -1 ? '' : parts[i].slice(at + 1).trim();
            if (key === 'max-age' && /^-?\\d+$/.test(val)) maxAge = Number(val);
            if (key === 'expires') { var t = Date.parse(val); if (!isNaN(t)) expires = t; }
          }
          var expired = maxAge !== null ? maxAge <= 0 : (expires !== null && expires <= Date.now());
          if (expired) jar.delete(name); else jar.set(name, value);
        }
      });
    } catch (e) {}
  }

  // ─── history.pushState / replaceState: in an inline srcdoc preview a RELATIVE
  // URL ('#/about', '?q=1') throws SecurityError — it resolves against the
  // PARENT page's base URL, a different origin — while the same URL made
  // absolute on the frame's OWN address ('about:srcdoc#/about') works. A path
  // ('/about') can't be expressed on about:srcdoc at all. On /s/ nothing throws.
  // So: try the real call; on a SecurityError retry with the URL resolved
  // against location.href (hash routers then really navigate); if that fails
  // too, keep the state with a state-only REPLACE — never a push, so a guard
  // that retries until its route matches can't flood the tab's shared history.
  // Any other error (DataCloneError) still reaches the page. Opaque origins only.
  if (String(window.origin) === 'null' && window.history) {
    var realReplace = history.replaceState;
    var wrap = function(real){
      if (typeof real !== 'function' || real.__papanShim) return real;
      var wrapped = function(state, title, url){
        try { return real.apply(this, arguments) }
        catch (e) {
          if (!e || e.name !== 'SecurityError' || arguments.length < 3 || url == null) throw e;
          try { return real.call(this, state, title, new URL(String(url), location.href).href) } catch (e2) {}
          try { return realReplace.call(this, state, title) } catch (e3) {}
        }
      };
      wrapped.__papanShim = true;
      return wrapped;
    };
    try {
      history.pushState = wrap(history.pushState);
      history.replaceState = wrap(realReplace);
    } catch (e) {}
  }

  // ─── Camera, microphone, screen (#1408 stage 1): browsers never give an
  // opaque-origin page these, whatever the frame's allow= says (Chrome throws
  // SecurityError). The call still fails exactly as before — the same error
  // reaches the page — but the parent is told, so Papan can say why and offer
  // the app's own link, where they work. Opaque origins only: on a real origin
  // a refusal is the person's answer and nothing is reported.
  var media = navigator.mediaDevices;
  if (String(window.origin) === 'null' && media) {
    var refused = function(e){ return !!e && (e.name === 'SecurityError' || e.name === 'NotAllowedError') };
    var report = function(kind){
      try { parent.postMessage({ type: 'papan:needs-app-origin', kind: kind }, '*') } catch (e) {}
    };
    var wrapMedia = function(name, kind){
      var real = media[name];
      if (typeof real !== 'function' || real.__papanShim) return;
      var wrapped = function(){
        var p;
        try { p = real.apply(this, arguments) }
        catch (e) { if (refused(e)) report(kind); throw e }
        return Promise.resolve(p).catch(function(e){ if (refused(e)) report(kind); throw e });
      };
      wrapped.__papanShim = true;
      try { media[name] = wrapped } catch (e) {}
    };
    wrapMedia('getUserMedia', 'media');
    wrapMedia('getDisplayMedia', 'screen');
  }
})();
</script>`
}
